import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FieldError, FieldInfo } from '../../../../hybrid/wrappers';
import { initFormHandlers } from '../../utils/formUtils';
import FromInputTitle from '../FormFieldTitle/FormFieldTitle';
import { getInputStyles } from '../form-input/helpers';
import { getDefaultVariation, getIsCoupleVariation } from '../helpers';
import { UserProfileContext } from '../../../../common/utils/getContext';
import css from './FormInputArea.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../common/utils/styles';
import FromControl from '../FromControl/FromControl';

const FormInputArea = ({
    attribute,
    hint,
    placeholder,
    required,
    disabled,
    title,
    handlers = {},
    compStyle,
    value,
    type,
    setFormData,
    info,
    errors,
    rows,
    maxLength = 255,
    variation,
    hideLengthInfo = false
}) => {
    const { stylers, classes } = generateCompStylers(compStyle);
    const { adminPage } = useContext(UserProfileContext);

    const fieldId = useMemo(() => {
        return `${attribute || ''}_${Math.floor(Math.random() * 10000000)}_field`;
    }, [attribute]);
    const profileContext = useContext(UserProfileContext) || {};
    const variationStyle = variation || getDefaultVariation(profileContext, title);
    const [focused, setFocused] = useState(false);
    const [val, setVal] = useState(value || '');
    const changeHandler = useCallback(
        (v) => {
            if (!disabled) {
                const valCor = v.length > maxLength ? v.substr(0, maxLength - 1) : v;
                setFormData(attribute, valCor);
                setVal(valCor);
            }
        },
        [disabled, maxLength, setFormData, attribute]
    );
    useEffect(() => {
        setVal(value || '');
    }, [setVal, value, attribute]);
    const initHandlers = useMemo(
        () => initFormHandlers(handlers, attribute, val, type, errors, setFocused),
        [handlers, attribute, val, type, errors]
    );
    const componentConfig = useMemo(
        () => ({
            ...initHandlers,
            onChange: (e) => changeHandler(e.target.value),
            required,
            value: val,
            placeholder: adminPage ? placeholder : '',
            maxLength,
            rows: rows || 3,
            disabled: Boolean(disabled) || false,
            className: classnames(css.input, classes.input),
            style: getInputStyles(stylers, errors, focused, disabled, null),
            id: fieldId
        }),
        [
            changeHandler,
            fieldId,
            classes,
            placeholder,
            disabled,
            errors,
            focused,
            initHandlers,
            maxLength,
            required,
            rows,
            stylers,
            val
        ]
    );

    return (
        <>
            <div style={stylers.wrapper} className={classnames(css.formGroup, classes.wrapper, css[variationStyle])}>
                {!!title && !getIsCoupleVariation(variationStyle) ? (
                    <FromInputTitle
                        id={fieldId}
                        errors={errors}
                        title={title}
                        compStyle={compStyle}
                        hint={hint}
                        required={required}
                        variationStyle={variationStyle}
                        focused={focused}
                    />
                ) : null}
                <label
                    htmlFor={fieldId}
                    style={{
                        ...stylers.input,
                        ...(errors && errors.length
                            ? stylers.inputError
                            : {
                                  ...(focused ? stylers.inputFocus : {}),
                                  ...(disabled ? stylers.inputDisabled : {})
                              })
                    }}
                    className={classnames(
                        css[`${variationStyle}--formControl`],
                        { [css['no-border']]: stylers.input?.border === 'none' },
                        classes.input,
                        errors && errors.length
                            ? [css[`${variationStyle}--formControl--error`], classes.inputError]
                            : focused
                            ? [css[`${variationStyle}--formControl--focus`], classes.inputFocus]
                            : '',
                        disabled && !errors?.length ? [css['formControl--disabled'], classes.inputDisabled] : ''
                    )}
                >
                    <div
                        style={stylers.formControlContent}
                        className={classnames(css.formControlContent, classes.formControlContent)}
                    >
                        <div
                            style={stylers.formControlInner}
                            className={classnames(css.formControlInner, classes.formControlInner)}
                        >
                            {!!title && getIsCoupleVariation(variationStyle) ? (
                                <FromInputTitle
                                    id={fieldId}
                                    errors={errors}
                                    title={title}
                                    compStyle={compStyle}
                                    hint={hint}
                                    required={required}
                                    isFilled={Boolean(val)}
                                    variationStyle={variationStyle}
                                    focused={focused}
                                />
                            ) : null}
                            <FromControl
                                {...{
                                    placeholder: '',
                                    compStyle,
                                    customCss: {
                                        inner: css.formControlInnerPlaceholderArea,
                                        wrapper: {
                                            className: classnames(
                                                css.formControlContentPlaceholderArea,
                                                classes.formControlContentPlaceholderArea
                                            ),
                                            ...(stylers.formControlContentPlaceholderArea || {})
                                        }
                                    },
                                    title,
                                    errors,
                                    required,
                                    val,
                                    focused,
                                    disabled
                                }}
                            >
                                <textarea {...componentConfig}> {val} </textarea>
                            </FromControl>
                        </div>
                    </div>
                </label>
                {!hideLengthInfo ? (
                    <div className={css.formControlTextAreaLengthInfo}>{`${val?.length} / ${maxLength}`}</div>
                ) : null}
                {!!info && <FieldInfo compStyle={compStyle?.info}>{info}</FieldInfo>}
                {errors && errors.length
                    ? errors.map((err, idx) => (
                          <FieldError compStyle={compStyle?.error} key={`${attribute}${idx}`}>
                              {err}
                          </FieldError>
                      ))
                    : null}
            </div>
        </>
    );
};
FormInputArea.propTypes = {
    attribute: PropTypes.string,
    hint: PropTypes.node,
    placeholder: PropTypes.node,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    title: PropTypes.object,
    handlers: PropTypes.object,
    compStyle: PropTypes.object,
    value: PropTypes.string,
    type: PropTypes.string,
    setFormData: PropTypes.func,
    info: PropTypes.node,
    errors: PropTypes.array,
    rows: PropTypes.number,
    maxLength: PropTypes.number,
    variation: PropTypes.string
};
export default FormInputArea;
